import {type DefaultError, useMutation, useQueryClient} from "@tanstack/react-query";
import {isAxiosError} from "axios";
import {queryHttp} from "~/query/queryHttp";
import type {m_taxonomy} from "~/services/classes/TaxonomyClasses";
import {notificationStore} from "~/stores/init";
import {taxonomyApprovalRequestsUrl, taxonomyDetailUrl} from "~/pages/taxonomy-builder-v2/services/taxonomyBuilderUrls";

export type SendTaxonomyForApprovalVariables = {
    taxonomy: m_taxonomy.FullSerializer;
    /** For the approvers; may be empty */
    notes: string;
};

function errorMessage(error: unknown): string {
    if (isAxiosError(error) && error.response) {
        // e.g. 400 while another approval request is still open
        const data = error.response.data as {message?: string, detail?: string, non_field_errors?: string[]} | undefined;
        const reason = data?.message ?? data?.detail ?? data?.non_field_errors?.[0];
        if (reason) return `Failed to send for approval. ${reason}`;
    }
    return 'Failed to send for approval. Please try again.';
}

/**
 * `POST approve/taxonomy/` with the taxonomy and the notes, as the current builder does; the backend emails
 * the approvers. The taxonomy is reloaded afterwards: it now has an open approval request, so it can't be
 * edited until that request is concluded.
 */
export function useSendTaxonomyForApprovalMutation() {
    const queryClient = useQueryClient();

    return useMutation<unknown, DefaultError, SendTaxonomyForApprovalVariables>({
        mutationFn: async ({taxonomy, notes}) =>
            (await queryHttp.post(taxonomyApprovalRequestsUrl(), {taxonomy: taxonomy.id, notes})).data,
        onSuccess: (_, {taxonomy}) => {
            void queryClient.invalidateQueries({queryKey: [taxonomyDetailUrl(taxonomy.id)]});
            notificationStore.show('Sent for approval.', {
                severity: 'success',
                id: `taxonomy-send-for-approval-success-${taxonomy.id}`,
                autoHideDuration: 3000,
            });
        },
        onError: (error, {taxonomy}) => {
            notificationStore.show(errorMessage(error), {
                severity: 'error',
                id: `taxonomy-send-for-approval-error-${taxonomy.id}`,
                autoHideDuration: 6000,
            });
        },
    });
}
